/**
 * Fade whatever is standing in front of the face you are looking at.
 *
 * Six of eighteen shirt faces are behind something — eight on pants — so
 * lighting them up does nothing visible unless the blocker gets out of the way.
 * `occludersOf` has always known which parts those are and has never been
 * called by anything.
 *
 * **Written for two callers.** The homepage try band wants it, and the editor
 * wants it more: painting a sleeve's inner face currently means orbiting and
 * squinting. So the policy lives here rather than in either shell, the diffing
 * lives in `shared/ghost-plan.ts` where it can be tested without WebGL, and
 * this file holds only the part that needs a viewer.
 *
 * Typed against `GhostTarget` rather than `ViewerHandle` on purpose: it is the
 * two methods this actually uses, which keeps it mockable and keeps a future
 * caller from having to own a whole viewer to reuse the behaviour.
 */

import type { RegionId } from "../../shared/format";
import { ghostPlan } from "../../shared/ghost-plan";

/** The slice of a viewer this needs. Anything with these two can be ghosted. */
export interface GhostTarget {
  occludersOf(region: RegionId): string[];
  setPartAlpha(part: string, alpha: number, opts?: { animate?: boolean }): void;
  /**
   * Which part a face belongs to, if the viewer can say.
   *
   * Optional because `reveal` never needed it and a caller mocking this should
   * not have to grow a method to keep compiling. `revealAll` uses it to avoid
   * fading a part whose own faces are in the selection.
   */
  partOf?(region: RegionId): string | null;
}

export interface GhostOptions {
  /**
   * How far to fade a blocker. Not zero: a limb that disappears reads as a bug
   * and takes the body's shape with it, and the whole point is to show *where*
   * a face sits.
   */
  alpha?: number;
  /**
   * Animate the fade. Defaults to honouring `prefers-reduced-motion`, so the
   * caller gets the right behaviour without having to remember.
   */
  animate?: boolean;
}

export interface GhostController {
  /** Ghost whatever blocks this region, restoring anything no longer needed. */
  reveal(region: RegionId | null): void;
  /**
   * The same, for a set of faces at once — what a face SELECTION needs.
   *
   * The homepage reveals one region at a time because it highlights one at a
   * time. The editor's selection is a set, and asking for the union is not the
   * same as revealing each in turn: doing them one by one would restore the
   * first face's blockers before the second's were applied, which is the blink
   * `ghostPlan` exists to prevent.
   *
   * An empty set puts the body back together, so a caller can pass its
   * selection straight through without branching on whether one exists.
   */
  revealAll(regions: readonly RegionId[]): void;
  /** Put the body back together. */
  clear(): void;
  /** Currently faded, for tests and for anyone rendering an "X hidden" note. */
  hidden(): string[];
}

/*
 * High enough to still read as a limb.
 *
 * 0.12 was the plan's number and it is wrong against a dark backdrop: the arm
 * vanished completely, which is the amputated look the ghost exists to avoid.
 * Checked on screen, not reasoned about.
 */
const GHOST_ALPHA = 0.22;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export function createGhostController(
  target: GhostTarget,
  opts: GhostOptions = {},
): GhostController {
  const alpha = opts.alpha ?? GHOST_ALPHA;
  /*
   * Reduced motion means jump to the end state, not skip the ghosting.
   * Somebody who asked for less movement still needs to see the face.
   */
  const animate = opts.animate ?? !prefersReducedMotion();

  /** What this controller has faded. Its own, not the viewer's. */
  let faded: string[] = [];

  function apply(next: string[]) {
    const plan = ghostPlan(faded, next);
    for (const part of plan.restore) target.setPartAlpha(part, 1, { animate });
    for (const part of plan.ghost) target.setPartAlpha(part, alpha, { animate });
    // `keep` is deliberately untouched: re-fading an already-faded part
    // restarts its tween and flickers.
    faded = [...plan.keep, ...plan.ghost];
  }

  return {
    reveal(region) {
      /*
       * The commonest path by far — twelve of eighteen shirt faces are
       * unobstructed — and it still has to run, because moving *to* a clear
       * face from a blocked one is what puts the body back together.
       */
      apply(region ? target.occludersOf(region) : []);
    },
    revealAll(regions) {
      const want = new Set<string>();
      for (const region of regions) {
        for (const part of target.occludersOf(region)) want.add(part);
      }
      /*
       * Never fade a part the selection is itself working on.
       *
       * A face and the thing blocking it can belong to the same part once a
       * selection spans several faces — pick a whole sleeve and its inner face
       * asks for the torso, while the torso's own faces may be in the same
       * selection. Ghosting a part somebody just chose to paint is the one
       * outcome that is simply wrong, so it is excluded rather than ranked.
       */
      const own = new Set<string>();
      for (const region of regions) {
        const part = target.partOf?.(region);
        if (part) own.add(part);
      }
      apply([...want].filter((p) => !own.has(p)));
    },
    clear() {
      apply([]);
    },
    hidden() {
      return [...faded];
    },
  };
}
